import { zodResolver } from '@hookform/resolvers/zod';
import { IBrand } from '../../types/brand';
import { IAgent } from '../../types/agent';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { updateBrand } from '../../hooks/mutations';
import { useThemeStore } from '../../state/theme';
import { BtnPrimary } from '../common/Buttons';
import Select from 'react-select';
import { LoaderCircle, Calendar } from 'lucide-react';
import { useState, useEffect } from 'react';

const OffboardAgentSchema = z.object({
  agents: z.array(z.string()),
  offboardingDate: z.string().optional(),
});

type OffboardAgentSchemaType = z.infer<typeof OffboardAgentSchema>;

interface OffboardAgentProps {
  selectedBrand: IBrand;
  brandAgents: IAgent[];
  onSuccess: () => void;
  onClose: () => void;
}

const OffboardAgent: React.FC<OffboardAgentProps> = ({
  selectedBrand,
  brandAgents,
  onSuccess,
  onClose,
}) => {
  const { isDarkMode } = useThemeStore();
  const [originalAgents] = useState<string[]>(selectedBrand?.agents || []);
  const [removingAgents, setRemovingAgents] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const { mutate: updateBrandMutation, isPending: isUpdatingBrand } =
    updateBrand({
      onSuccess: () => {
        onSuccess();
      },
      onError: (error) => {
        console.error('Failed to offboard agents:', error);
      },
    });

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm<OffboardAgentSchemaType>({
    resolver: zodResolver(OffboardAgentSchema),
    defaultValues: {
      agents: selectedBrand?.agents || [],
      offboardingDate: getTodayDate(),
    },
  });

  const watchedAgents = watch('agents');

  useEffect(() => {
    // Check if any agents are being removed
    const isRemoving = originalAgents.length > watchedAgents.length;
    setRemovingAgents(isRemoving);
  }, [watchedAgents, originalAgents]);

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : 'white',
    }),
    option: (
      base: any,
      state: { isFocused: boolean; isSelected: boolean }
    ) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#4F46E5'
        : state.isFocused
          ? isDarkMode
            ? '#4B5563'
            : '#F3F4F6'
          : 'transparent',
      color: state.isSelected ? 'white' : isDarkMode ? '#F3F4F6' : '#111827',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#4B5563' : '#E5E7EB',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDarkMode ? '#F3F4F6' : '#111827',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      ':hover': {
        backgroundColor: isDarkMode ? '#6B7280' : '#D1D5DB',
      },
    }),
  };

  const onSubmit = (data: OffboardAgentSchemaType) => {
    const brandData = {
      _id: selectedBrand._id,
      clientId: selectedBrand.clientId,
      name: selectedBrand.name,
      websiteLink: selectedBrand.websiteLink,
      agents: data.agents,
      resources: selectedBrand.resources || [],
      offboardingDate: removingAgents ? data.offboardingDate : undefined,
    };

    updateBrandMutation(brandData);
  };

  const agentOptions = brandAgents.map((agent) => ({
    value: agent.kimId,
    label: agent.name,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          Remove agents from this brand by deselecting them below. You'll need to
          specify an offboarding date when removing agents from{' '}
          <strong>{selectedBrand.name}</strong>.
        </p>
      </div>

      <div>
        <label htmlFor="agents" className={labelClassName}>
          Assigned Agents
        </label>
        <Controller
          name="agents"
          control={control}
          render={({ field }) => {
            const selectedOptions = brandAgents
              .filter((agent) => field.value?.includes(agent.kimId))
              .map((agent) => ({
                value: agent.kimId,
                label: agent.name,
              }));

            return (
              <Select
                {...field}
                isMulti
                value={selectedOptions}
                options={agentOptions}
                styles={selectStyles}
                className="mt-1"
                placeholder="No agents assigned"
                onChange={(selected) => {
                  const selectedKimIds = selected.map((option) => option.value);
                  field.onChange(selectedKimIds);
                }}
              />
            );
          }}
        />

        {errors.agents && (
          <span className="text-red-500 text-sm">{errors.agents.message}</span>
        )}
      </div>

      {removingAgents && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 animate-in fade-in duration-200">
          <div className="flex items-start gap-3">
            <Calendar size={20} className="mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div className="flex-1">
              <label htmlFor="offboardingDate" className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Offboarding Date <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                Select the date when the removed agent(s) will be offboarded from this brand.
              </p>
              <input
                type="date"
                id="offboardingDate"
                {...register('offboardingDate')}
                className={inputClassName}
                max="9999-12-31"
              />
              {errors.offboardingDate && (
                <span className="text-red-500 text-sm mt-1 block">
                  {errors.offboardingDate.message}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {brandAgents.length === 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No agents are currently assigned to this brand.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <BtnPrimary disabled={isUpdatingBrand} type="submit">
          {isUpdatingBrand ? (
            <div className="flex items-center gap-2">
              <span>Updating</span>
              <LoaderCircle size={16} className="animate-spin" />
            </div>
          ) : (
            'Update Agents'
          )}
        </BtnPrimary>
      </div>
    </form>
  );
};

export default OffboardAgent;
